import { addDays } from "date-fns";
import { capitalize } from "lodash";
import moment from "moment";

export const parseObj = (stringItem) => {
  return stringItem ? JSON.parse(stringItem) : null;
};

export const parseInsuranceData = ({ params }) => {
  let payload = {
    startDate:
      params?.startDate || moment(addDays(new Date(), 7)).format("YYYY-MM-DD"),
    endDate:
      params?.endDate || moment(addDays(new Date(), 14)).format("YYYY-MM-DD"),
    destinationCountry: params?.destinationCountry || "South Africa",
    numberOfAdults: params?.numberOfAdults || 1,
    numberOfChildren: params?.numberOfChildren || 0,
    numberOfInfants: params?.numberOfInfants || 0,
    departure: parseObj(params?.departure) || null,
    arrival: parseObj(params?.arrival) || null,
    isRoundTrip: params?.isRoundTrip === "true" ? true : false,
  };
  if (params?.baggageAllowance) {
    payload.baggageAllowance = params?.baggageAllowance;
  }
  if (params?.flights) {
    payload.flights = parseObj(params?.flights) || null;
  }
  // console.log({ payload });
  return payload;
};

export const getInsuranceLocationsFromParams = ({ params }) => {
  const departure = params?.departure ? JSON.parse(params?.departure) : null;
  const arrival = params?.arrival ? JSON.parse(params?.arrival) : null;
  return {
    departure: departure?.city || "",
    arrival: arrival?.city || "",
  };
};

export const getInsuranceDatesFromParams = ({ params, dateFormats }) => {
  return {
    startDate: params?.startDate
      ? moment(params?.startDate).format(dateFormats || "ddd, DD MMM")
      : "",
    endDate: params?.endDate
      ? moment(params?.endDate).format(dateFormats || "ddd, DD MMM")
      : "",
  };
};

export const calculateInsuranceDuration = ({ startDate, endDate }) => {
  if (!startDate || !endDate) {
    return "Invalid dates provided";
  }

  const start = moment(startDate);
  const end = moment(endDate);

  if (!start.isValid() || !end.isValid()) {
    return "Invalid date format";
  }

  const duration = moment.duration(end.diff(start));

  const years = duration.years();
  const months = duration.months();
  const days = duration.days();

  let result = "";

  if (years > 0) {
    result += `${years} year${years > 1 ? "s" : ""} `;
  }
  if (months > 0) {
    result += `${months} month${months > 1 ? "s" : ""} `;
  }
  if (days > 0) {
    result += `${days} day${days > 1 ? "s" : ""}`;
  }

  return result.trim() || "0 days";
};

export const insuranceLogo = (path) => {
  return `https://dembe.onrender.com${path}`;
};

export const calculateCartInsuranceDuration = ({ item }) => {
  let start = item?.item?.req?.startDate;
  let end = item?.item?.req?.endDate;

  if (!start || !end) return "0 days";

  const startDate = moment(start, "YYYY-MM-DD");
  const endDate = moment(end, "YYYY-MM-DD");

  const diffInDays = endDate.diff(startDate, "days");

  return diffInDays > 0 ? `${diffInDays} days` : "0 days";
};

export const cartInsurancePassengerMix = ({ item }) => {
  let passengerMix = "";

  const numberOfAdults = item?.item?.req?.numberOfAdults || 0;
  const numberOfChildren = item?.item?.req?.numberOfChildren || 0;
  const numberOfInfants = item?.item?.req?.numberOfInfants || 0;

  const mixArray = [];
  if (numberOfAdults > 0)
    mixArray.push(`${numberOfAdults} Adult${numberOfAdults > 1 ? "s" : ""}`);
  if (numberOfChildren > 0)
    mixArray.push(
      `${numberOfChildren} Child${numberOfChildren > 1 ? "ren" : ""}`
    );
  if (numberOfInfants > 0)
    mixArray.push(`${numberOfInfants} Infant${numberOfInfants > 1 ? "s" : ""}`);

  passengerMix = mixArray.join(", ");

  return passengerMix;
};

export const parseInsuranceTravellerData = (travellers) => {
  return travellers?.map((traveller) => {
    let travellerData = [
      { label: "Email", value: traveller?.email },
      { label: "Phone number", value: traveller?.phone },
      {
        label: "Type",
        value: capitalize(traveller?.ageCategory || ""),
      },
    ];
    let travellerDetailData = [
      {
        label: "Date of birth",
        value: moment(traveller?.birthDate).format("YYYY-MM-DD"),
      },
      { label: "Nationality", value: traveller?.countryCode },
      {
        label: "Passport No",
        value: traveller?.passportNumber,
      },
      {
        label: "State",
        value: traveller?.state,
      },
      {
        label: "City",
        value: traveller?.city,
      },
      {
        label: "Gender",
        value: traveller?.gender,
      },
      {
        label: "Next of kin name",
        value: traveller?.nextOfKinName,
      },
      {
        label: "Next of kin relationship",
        value: traveller?.nextOfKinRelationship,
      },
      {
        label: "Next of kin phone",
        value: traveller?.nextOfKinContact,
      },
      {
        label: "Next of kin address",
        value: traveller?.nextOfKinAddress,
      },
    ];
    if (Array.isArray(traveller?.medical)) {
      travellerDetailData = travellerDetailData.concat(
        traveller?.medical.map((medical) => {
          return {
            label: medical?.question,
            value: String(medical?.answer),
          };
        })
      );
    }
    return {
      name: `${traveller?.title} ${traveller?.lastName}/${traveller?.firstName} ${traveller?.middleName}`,
      detail: false,
      travellerData,
      travellerDetailData,
    };
  });
};

export const isRoundTripFlight = (booking) => {
  const destinations = booking?.flightObj?.destinations;

  if (!Array.isArray(destinations) || destinations.length === 0) {
    return false;
  }

  // Single flight
  if (destinations.length === 1) {
    return false;
  }

  // Check if return destination matches origin
  if (destinations.length === 2) {
    const firstFlight = destinations[0];
    const secondFlight = destinations[1];

    return firstFlight.from === secondFlight.to;
  }

  return false;
};

export const isMulticityTripFlight = (booking) => {
  if (isRoundTripFlight(booking)) return false;
  const destinations = booking?.flightObj?.destinations;

  if (!Array.isArray(destinations) || destinations.length === 0) {
    return false;
  }

  // Single flight
  if (destinations.length === 1) {
    return false;
  }

  return true;
};

export const insuranceAddonGroupController = (policy) => {
  if (!policy?.addons || policy.addons.length === 0) return [];
  const items = [
    { id: "luggage", title: "Baggage" },
    { id: "lounge", title: "Lounge" },
    // { id: "payout", title: "Payout" },
  ];
  const groups = items
    .map((item) => {
      let addons = policy.addons.filter((addon) => addon.id.includes(item.id));
      return {
        ...item,
        addons,
        description: addons[0]?.description,
      };
    })
    .filter((item) => Boolean(item.addons.length));
  return groups;
};
